

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import RobotChatIcon from '@/components/RobotChatIcon';
import CoverLetterIcon from '@/components/CoverLetterIcon';
import RecruiterLensIcon from '@/components/RecruiterLensIcon';

interface ConfirmationModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  fileName: string | null;
  action: string | null;
  isAnalyzing: boolean;
}

export const ConfirmationModalContent: React.FC<ConfirmationModalContentProps> = ({
  isOpen,
  onClose,
  onConfirm,
  fileName,
  action,
  isAnalyzing,
}) => {
  const getActionIcon = () => {
    if (action === "Resume Critique Bot") return RobotChatIcon;
    if (action === "Cover Letter Generator") return CoverLetterIcon;
    if (action === "Recruiter Lens") return RecruiterLensIcon;
    return CheckCircle;
  };

  const ActionIcon = getActionIcon();
  if (!isOpen) {
    return null;
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md bg-gradient-to-br from-background via-background to-background/95 backdrop-blur-sm border border-border/50 shadow-2xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <AlertDialogHeader className="text-center space-y-4 pb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="mx-auto w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center border border-primary/20"
            >
              <div className="text-primary">
                <ActionIcon className="h-8 w-8" />
              </div>
            </motion.div>
            
            <div className="space-y-2">
              <AlertDialogTitle className="text-xl font-bold text-foreground">
                Confirm AI Analysis
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-muted-foreground leading-relaxed">
                Ready to analyze your resume with advanced AI technology?
              </AlertDialogDescription>
            </div>
          </AlertDialogHeader>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4 pb-6"
          >
            {/* File Information */}
            <div className="bg-card/50 rounded-lg p-4 border border-border/30">
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {fileName || 'Resume File'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Ready for analysis
                  </p>
                </div>
              </div>
            </div>

            {/* Action Information */}
            <div className="bg-card/50 rounded-lg p-4 border border-border/30">
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {action || 'AI Analysis'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Powered by vTech AI
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Notice */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-green-500/10 border border-green-500/20 rounded-lg p-3"
            >
              <div className="flex items-start space-x-2">
                <Lock className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-400">
                  <p className="font-medium">Privacy Protected</p>
                  <p>Your resume will be analyzed using a masked version to protect your personal information.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <AlertDialogFooter className="flex-col sm:flex-row gap-3 pt-4 border-t border-border/30">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full sm:w-auto"
            >
              <AlertDialogCancel 
                onClick={onClose} 
                disabled={isAnalyzing}
                className="w-full sm:w-auto bg-background border-border hover:bg-muted/50 transition-colors"
              >
                Cancel
              </AlertDialogCancel>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full sm:w-auto"
            >
              <Button 
                onClick={onConfirm} 
                disabled={isAnalyzing}
                className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <AnimatePresence mode="wait">
                  {isAnalyzing ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center space-x-2"
                    >
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Analyzing...</span>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <span>Start Analysis</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
            </motion.div>
          </AlertDialogFooter>
        </motion.div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
